"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  FileText,
  MessageSquare,
  BookOpen,
  Quote,
  Check,
  ChevronRight,
  Lightbulb,
  ListChecks,
  PenLine,
  Sigma,
  BrainCircuit,
} from "lucide-react";
import { clsx } from "clsx";

type StepKey =
  | "ask"
  | "retrieve"
  | "cite"
  | "explain"
  | "worked"
  | "pattern"
  | "tip"
  | "revision"
  | "answer";

const STEPS: { key: StepKey; label: string; icon: typeof Sparkles }[] = [
  { key: "ask", label: "Ask", icon: MessageSquare },
  { key: "retrieve", label: "Retrieve", icon: BookOpen },
  { key: "cite", label: "Cite", icon: FileText },
  { key: "explain", label: "Explain", icon: Lightbulb },
  { key: "worked", label: "Worked", icon: PenLine },
  { key: "pattern", label: "Pattern", icon: Sigma },
  { key: "tip", label: "Exam Tip", icon: BrainCircuit },
  { key: "revision", label: "Revise", icon: ListChecks },
  { key: "answer", label: "Answer", icon: Sparkles },
];

const QUESTION = "Find the derivative of 3x⁴ − 2x³ + 5x − 7 at x = 2";

const SOURCES = [
  {
    course: "MST102",
    title: "Calculus I — Power Rule",
    meta: "Lecture notes · p.42",
  },
  {
    course: "MST102",
    title: "Worked Examples — Polynomials",
    meta: "Lecture notes · p.88",
  },
  {
    course: "MST102",
    title: "Past Questions — 2019/2020",
    meta: "Dept MST · Q.7",
  },
];

const ANNOTATIONS = [
  { label: "Cited from your notes", side: "left" as const },
  { label: "Exam-ready format", side: "right" as const },
  { label: "Talks like your professor", side: "left" as const },
];

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.4 },
};

export function HeroProductDemo() {
  const [step, setStep] = useState<StepKey>("ask");
  const [paused, setPaused] = useState(false);

  // Step timing: advance strictly 1→2→…→9→1. A chained setTimeout (instead of
  // setInterval) guarantees_each_step_fully_settles_before_the_next_fires, so the
  // loop never skips ahead (e.g. 2 → 9) when transitions overlap.
  const STEP_MS = 4200;
  useEffect(() => {
    if (paused) return;
    let timer: number;
    const order: StepKey[] = [
      "ask",
      "retrieve",
      "cite",
      "explain",
      "worked",
      "pattern",
      "tip",
      "revision",
      "answer",
    ];
    const tick = () => {
      setStep((current) => order[(order.indexOf(current) + 1) % order.length]);
      timer = window.setTimeout(tick, STEP_MS);
    };
    timer = window.setTimeout(tick, STEP_MS);
    return () => window.clearTimeout(timer);
  }, [paused]);

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto w-full">
        {/* Stepper */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {STEPS.map((s, i) => {
            const active = s.key === step;
            const StepIcon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setStep(s.key)}
                className={clsx(
                  "group flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all",
                  active
                    ? "border-y bg-y text-black shadow-[0_0_24px_rgba(255,214,0,0.3)]"
                    : "border-border text-foreground hover:border-y/60",
                )}
              >
                <span
                  className={clsx(
                    "flex h-5 w-5 items-center justify-center rounded-full text-xs font-black",
                    active ? "bg-black text-y" : "bg-y/15 text-y",
                  )}
                >
                  {i + 1}
                </span>
                <StepIcon size={14} />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* 2D product window */}
        <div className="relative mx-auto w-full">
          <div className="matte-metal rounded-2xl border border-y/40 shadow-2xl overflow-hidden">
            {/* window chrome */}
            <div className="flex items-center gap-2 border-b border-border/70 bg-background/60 px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-red-400/80" />
              <span className="h-3 w-3 rounded-full bg-yellow-400/80" />
              <span className="h-3 w-3 rounded-full bg-green-400/80" />
              <span className="ml-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                UniUI · Ask AI
              </span>
            </div>

            <div className="min-h-[20rem] px-5 py-6 md:px-8 md:py-8">
              <AnimatePresence mode="wait">
                {step === "ask" && (
                  <motion.div key="ask" {...fade} className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-foreground px-4 py-3 text-sm font-medium text-background">
                      {QUESTION}
                      <span className="ml-1 inline-block h-4 w-[2px] translate-y-1 animate-pulse bg-background/70" />
                    </div>
                  </motion.div>
                )}

                {step === "retrieve" && (
                  <motion.div key="retrieve" {...fade} className="space-y-3">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-y">
                      <BookOpen size={14} /> Retrieving from your materials
                    </p>
                    {SOURCES.map((src, i) => (
                      <motion.div
                        key={src.title}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.15, duration: 0.3 }}
                        className="flex items-center gap-3 rounded-xl border border-border bg-background/60 p-3"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-y/15 text-y">
                          <FileText size={16} />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-foreground">
                            {src.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {src.course} · {src.meta}
                          </p>
                        </div>
                        <Check size={16} className="ml-auto shrink-0 text-y" />
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {step === "cite" && (
                  <motion.div key="cite" {...fade} className="space-y-3">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-y">
                      <Quote size={14} /> Source-cited, page by page
                    </p>
                    {[
                      "MST102 · p.42 — Power Rule",
                      "MST102 · p.88 — Polynomial examples",
                      "Dept MST · Past Q.7 (2019/20)",
                    ].map((cite, i) => (
                      <motion.div
                        key={cite}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.15, duration: 0.3 }}
                        className="flex items-center gap-3 rounded-xl border border-y/30 bg-y/5 p-3"
                      >
                        <Quote size={15} className="shrink-0 text-y" />
                        <span className="text-xs font-semibold text-foreground">
                          {cite}
                        </span>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {step === "explain" && (
                  <motion.div key="explain" {...fade} className="space-y-3">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-y">
                      <Lightbulb size={14} /> Direct Explanation
                    </p>
                    <p className="text-sm font-semibold leading-relaxed text-foreground">
                      Use the power rule{" "}
                      <span className="font-mono">term by term</span>: bring the
                      index down, reduce the power by one, then substitute x = 2
                      at the end.
                    </p>
                    <p className="text-xs leading-relaxed text-foreground">
                      Each term of 3x⁴ − 2x³ + 5x − 7 is treated independently —
                      constants vanish because their rate of change is zero.
                    </p>
                  </motion.div>
                )}

                {step === "worked" && (
                  <motion.div key="worked" {...fade} className="space-y-3">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-y">
                      <PenLine size={14} /> Worked Steps
                    </p>
                    {[
                      "f'(x) = 12x³ − 6x² + 5",
                      "f'(2) = 12(8) − 6(4) + 5",
                      "= 96 − 24 + 5",
                      "= 77",
                    ].map((line, i) => (
                      <motion.p
                        key={line}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.2, duration: 0.3 }}
                        className="font-mono text-sm text-foreground"
                      >
                        {line}
                      </motion.p>
                    ))}
                  </motion.div>
                )}

                {step === "pattern" && (
                  <motion.div key="pattern" {...fade}>
                    <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-y">
                      <Sigma size={14} /> General Pattern
                    </p>
                    <div className="rounded-2xl bg-y px-4 py-3">
                      <p className="font-mono text-[15px] font-black text-black">
                        d/dx[xⁿ] = n · xⁿ⁻¹
                      </p>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-foreground">
                      This rule applies to every power term — memorise it once,
                      reuse it forever.
                    </p>
                  </motion.div>
                )}

                {step === "tip" && (
                  <motion.div key="tip" {...fade} className="space-y-2">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-y">
                      <BrainCircuit size={14} /> Exam Tip
                    </p>
                    <div className="rounded-2xl border border-y/30 bg-muted/30 p-4">
                      <p className="text-sm font-semibold leading-relaxed text-foreground">
                        Always show the derivative{" "}
                        <span className="text-y">before</span> substitution.
                        Examiners reward the rule, the working, and the final
                        answer separately — never skip a line.
                      </p>
                    </div>
                  </motion.div>
                )}

                {step === "revision" && (
                  <motion.div key="revision" {...fade} className="space-y-3">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-y">
                      <ListChecks size={14} /> Quick Revision Card
                    </p>
                    <ul className="space-y-2">
                      {[
                        "Power rule first",
                        "Substitute second",
                        "Box the_final_answer: 77",
                      ].map((line, i) => (
                        <motion.li
                          key={line}
                          initial={{ opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.18, duration: 0.3 }}
                          className="flex items-center gap-2 rounded-xl bg-y/10 border border-y/30 px-3 py-2 text-xs font-bold text-foreground"
                        >
                          <Check size={14} className="shrink-0 text-y" />
                          {line}
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                {step === "answer" && (
                  <motion.div key="answer" {...fade} className="space-y-4">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-y text-black">
                        <Sparkles size={16} />
                      </span>
                      <div className="min-w-0 space-y-2">
                        {[
                          "f'(x) = 12x³ − 6x² + 5",
                          "At x = 2: 96 − 24 + 5 = 77",
                        ].map((line, i) => (
                          <motion.p
                            key={line}
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.18, duration: 0.3 }}
                            className="font-mono text-sm text-foreground"
                          >
                            {line}
                          </motion.p>
                        ))}
                        <motion.p
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.7, duration: 0.3 }}
                          className="inline-flex items-center gap-2 rounded-full bg-y px-3 py-1 text-sm font-black text-black"
                        >
                          Final_Answer: 77 <Check size={14} />
                        </motion.p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 border-t border-border/70 pt-3">
                      {["MST102 · p.42", "MST102 · p.88", "Dept MST · Q.7"].map(
                        (cite) => (
                          <span
                            key={cite}
                            className="inline-flex items-center gap-1 rounded-md bg-y/10 px-2 py-1 text-xs font-bold text-y"
                          >
                            <Quote size={11} /> {cite}
                          </span>
                        ),
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* 2D annotation callouts */}
          <div className="pointer-events-none mt-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            {ANNOTATIONS.map((a) => (
              <span
                key={a.label}
                className={clsx(
                  "inline-flex items-center gap-1.5 rounded-full border border-y/40 bg-background/80 px-3 py-1.5 text-xs font-bold text-foreground shadow-sm",
                  a.side === "right" && "md:flex-row-reverse",
                )}
              >
                <ChevronRight size={12} className="text-y" />
                {a.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
